import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ChevronDown, ChevronUp, Flame, GraduationCap, Loader2, Mic, Send, Share2, Target, Users } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { ShareProgress } from "@/components/ShareProgress";
import { BadgeWall } from "@/components/BadgeWall";

const LEVEL_TONE = {
  confident: "border-emerald-400/50 bg-emerald-500/10 text-emerald-300",
  "getting there": "border-brand/50 bg-brand/10 text-brand",
  "starting out": "border-amber-400/50 bg-amber-500/10 text-amber-300",
};

const practise = (kind, lang) =>
  window.dispatchEvent(new CustomEvent("chatmilu:practise", { detail: { kind, lang } }));

const Stat = ({ icon: Icon, label, value, tone = "text-brand" }) => (
  <div className="rounded-3xl border border-white/10 bg-panel p-5">
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-ink-soft">
      <Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}
    </p>
    <p className="mt-2 font-display text-3xl font-extrabold tracking-tighter">{value}</p>
  </div>
);

export default function Progress() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [sharing, setSharing] = useState(false);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState("");
  const [nights, setNights] = useState(null);

  useEffect(() => {
    api.get("/wingman/journal").then((r) => setNights(r.data)).catch(() => setNights(null));
  }, []);

  const sendNotes = async (at) => {
    if (!to.trim()) return;
    setSending(at);
    try {
      await api.post("/wingman/calls/send", { at, to: to.trim() });
      toast.success(`Notes sent to ${to.trim()}`);
      setTo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send those notes");
    } finally {
      setSending("");
    }
  };

  useEffect(() => {
    api.get("/wingman/progress").then((r) => setData(r.data)).catch(() => setData({ languages: [] }));
  }, []);

  if (!data) {
    return (
      <div className="mx-auto max-w-6xl px-5 py-24 text-center text-ink-soft" data-testid="progress-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
      </div>
    );
  }

  const week = data.week || {};
  const pace = week.goal ? Math.min(100, Math.round((week.done / week.goal) * 100)) : 0;

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8" data-testid="progress-page">
      <Link to="/chat" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Back to chat
      </Link>

      <div className="mt-6 flex items-start gap-4">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/15 text-brand">
          <GraduationCap className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
            Your practice
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-soft md:text-base">
            Every practice call MiLu has marked, the languages you are building, and the lines that keep
            tripping you up.
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="progress-stats">
        <div className="rounded-3xl border border-white/10 bg-panel p-5">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-ink-soft">
            <Target className="h-3.5 w-3.5 text-brand" /> This week
          </p>
          <p className="mt-2 font-display text-3xl font-extrabold tracking-tighter"
             data-testid="progress-week">
            {week.done} <span className="text-lg text-ink-soft">/ {week.goal}</span>
          </p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
            <span className={`block h-full rounded-full transition-[width] duration-700 ${
              week.hit ? "bg-emerald-400" : "bg-brand"}`} style={{ width: `${pace}%` }} />
          </div>
          <p className="mt-2 text-xs text-ink-soft">
            {week.hit ? "Goal hit — MiLu is proud" : `${week.goal - week.done} more practice calls to go`}
          </p>
        </div>
        <Stat icon={Flame} label="Quiz streak" tone="text-orange-400"
              value={`${data.practice?.streak || 0} ${data.practice?.streak === 1 ? "day" : "days"}`} />
        <Stat icon={GraduationCap} label="Answers right"
              value={data.practice?.answered
                ? `${Math.round((data.practice.correct / data.practice.answered) * 100)}%`
                : "—"} />
      </div>

      <h2 className="mt-12 flex items-center gap-3 text-base font-semibold md:text-lg">
        Your languages
        {data.languages.length > 0 && (
          <button
            data-testid="share-progress-open"
            onClick={() => setSharing(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-brand/50 px-3 py-1 text-xs font-medium text-brand transition-colors hover:bg-brand/10"
          >
            <Share2 className="h-3 w-3" /> Share my progress
          </button>
        )}
        <Link
          data-testid="progress-buddy-link"
          to="/buddy"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs font-medium text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          <Users className="h-3 w-3" /> Practise with a friend
        </Link>
      </h2>
      {data.languages.length === 0 && (
        <p className="mt-3 text-sm text-ink-soft" data-testid="progress-empty">
          Hold a call with MiLu in a language you are learning — he will mark it and it will show up here.
        </p>
      )}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {data.languages.map((l) => (
          <div key={l.lang} data-testid={`progress-lang-${l.lang}`}
               className="rounded-3xl border border-white/10 bg-panel p-5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-xl font-bold tracking-tight">{l.name}</p>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                LEVEL_TONE[l.level]}`}>{l.level}</span>
              <span className="ms-auto text-xs text-ink-soft">
                {l.calls} call{l.calls === 1 ? "" : "s"} · avg {l.average} · best {l.best}
              </span>
            </div>
            <p className="mt-2 text-sm text-ink-soft">{l.next_step}</p>
            <div className="mt-4 h-36 w-full" data-testid={`progress-chart-${l.lang}`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={l.history} margin={{ top: 6, right: 6, bottom: 0, left: -22 }}>
                  <XAxis dataKey="at" tick={{ fill: "#7C8593", fontSize: 10 }} tickLine={false}
                         axisLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#7C8593", fontSize: 10 }} tickLine={false}
                         axisLine={false} />
                  <Tooltip contentStyle={{ background: "#111318", border: "1px solid rgba(255,255,255,.12)",
                                           borderRadius: 12, fontSize: 12 }} />
                  <Line type="monotone" dataKey="score" stroke="#1A7DFF" strokeWidth={2}
                        dot={{ r: 3, fill: "#22B8FF" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                data-testid={`progress-drill-${l.lang}`}
                onClick={() => practise("drill", l.lang)}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand/50 px-3 py-1 text-xs text-brand transition-colors hover:bg-brand/10"
              >
                <Mic className="h-3 w-3" /> Practise drills
              </button>
              <button
                data-testid={`progress-quiz-${l.lang}`}
                onClick={() => practise("quiz", l.lang)}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
              >
                <GraduationCap className="h-3 w-3" /> Daily quiz
              </button>
            </div>
          </div>
        ))}
      </div>

      {data.weak?.length > 0 && (
        <>
          <h2 className="mt-12 text-base font-semibold md:text-lg">Lines that keep tripping you up</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="progress-weak">
            {data.weak.map((w) => (
              <div key={w.text}
                   className="flex items-center gap-3 rounded-2xl border border-white/10 bg-panel px-4 py-3">
                <div className="min-w-0">
                  <p dir="auto" className="truncate text-sm text-ink">{w.text}</p>
                  {w.meaning && (
                    <p dir="auto" className="truncate text-xs text-ink-soft">{w.meaning}</p>
                  )}
                </div>
                <span className="ms-auto shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-300">
                  {w.times}×
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {data.lessons?.length > 0 && (
        <>
          <h2 className="mt-12 text-base font-semibold md:text-lg">Every lesson report</h2>
          <div className="mt-4 space-y-2" data-testid="progress-lessons">
            {data.lessons.map((l) => (
              <div key={l.at} className="rounded-2xl border border-white/10 bg-panel">
                <button
                  data-testid={`progress-lesson-${l.at}`}
                  onClick={() => setOpen(open === l.at ? "" : l.at)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-start"
                >
                  <span className="text-sm text-ink">{l.name}</span>
                  <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-semibold text-brand">
                    {l.score}/100
                  </span>
                  <span className="text-xs text-ink-soft">{l.at?.slice(0, 10)}</span>
                  {open === l.at
                    ? <ChevronUp className="ms-auto h-4 w-4 text-ink-soft" />
                    : <ChevronDown className="ms-auto h-4 w-4 text-ink-soft" />}
                </button>
                {open === l.at && (
                  <div className="border-t border-white/10 px-4 py-3">
                    {l.recap && <p dir="auto" className="text-sm text-ink-soft">{l.recap}</p>}
                    {l.wins?.map((w) => (
                      <p key={w} dir="auto" className="mt-1.5 text-xs text-emerald-300">✓ {w}</p>
                    ))}
                    {l.slips?.map((s) => (
                      <p key={s} dir="auto" className="mt-1.5 text-xs text-ink-soft">– {s}</p>
                    ))}
                    {l.practise && (
                      <p dir="auto" className="mt-2 text-xs text-ink">Worth practising: {l.practise}</p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        data-testid={`share-notes-email-${l.at}`}
                        type="email"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendNotes(l.at)}
                        placeholder="Email these notes to…"
                        className="min-w-[14rem] flex-1 rounded-full border border-white/15 bg-panel px-3 py-1.5 text-xs text-ink outline-none placeholder:text-ink-soft/70 focus:border-brand/60"
                      />
                      <button
                        data-testid={`share-notes-send-${l.at}`}
                        onClick={() => sendNotes(l.at)}
                        disabled={sending === l.at || !to.trim()}
                        className="inline-flex items-center gap-1.5 rounded-full border border-brand/50 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/10 disabled:opacity-50"
                      >
                        {sending === l.at
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Send className="h-3 w-3" />}
                        Send
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {nights?.nights?.length > 0 && (
        <>
          <h2 className="mt-12 text-base font-semibold md:text-lg">
            Your nights
            <span className="ms-3 text-xs font-normal text-ink-soft">
              {nights.run} night{nights.run === 1 ? "" : "s"} in a row · {nights.kept} kept
            </span>
          </h2>
          <div className="mt-4 space-y-2" data-testid="progress-nights">
            {nights.nights.slice().reverse().map((n) => (
              <div key={n.day}
                   className="flex items-start gap-3 rounded-2xl border border-white/10 bg-panel px-4 py-3">
                <span className="shrink-0 text-xs text-indigo-300">{n.day.slice(8)}/{n.day.slice(5, 7)}</span>
                <p dir="auto" className="text-sm text-ink">{n.note}</p>
              </div>
            ))}
          </div>
        </>
      )}

      <BadgeWall />

      <ShareProgress
        open={sharing}
        onOpenChange={setSharing}
        languages={data.languages}
        streak={data.practice?.streak || 0}
        week={week}
      />
    </div>
  );
}
